import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { WhatsappLogo, X } from "@phosphor-icons/react";
import { waLinkBusiness, pageContextMessage } from "../../lib/wa";

// WhatsApp business number is sourced from the frontend env so ops can rotate
// it without a code change. Set REACT_APP_WHATSAPP_BUSINESS_NUMBER to the
// digits-only number (with country code, no + or spaces). When unset the FAB
// is suppressed entirely so we don't surface a dead link to prospects.
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_BUSINESS_NUMBER || "";

// Master feature flag for the floating WhatsApp button. The FAB is hidden by
// default so we can ship without it during the soft launch — flip the env
// flag REACT_APP_WHATSAPP_FAB_ENABLED to "true" to bring it back without
// touching code.
const FAB_ENABLED = String(process.env.REACT_APP_WHATSAPP_FAB_ENABLED || "").toLowerCase() === "true";

// Portals where the public "Invisique Care" WhatsApp FAB is suppressed.
// Inside the portals, users already have purpose-built channels (Lab Chat,
// Patient Chat → "My Clinic", Support Tickets, etc.) — a floating WhatsApp
// button would cannibalise adoption of those surfaces. The FAB stays on
// marketing / landing / auth / smile-assessment pages where prospective
// patients and dentists are the audience.
const PORTAL_PREFIXES = ["/doctor", "/admin", "/coe", "/patient"];


export default function WhatsAppFab() {
    const [open, setOpen] = useState(false);
    const [mounted, setMounted] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 1200);
        return () => clearTimeout(t);
    }, []);

    // Master kill-switch: when the flag is not "true", suppress the FAB everywhere.
    if (!FAB_ENABLED) return null;
    if (!mounted) return null;
    // Skip the FAB on any portal route — see PORTAL_PREFIXES rationale above.
    if (PORTAL_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
        return null;
    }

    const link = waLinkBusiness(WHATSAPP_NUMBER, pageContextMessage(pathname));
    // No env-configured number → don't surface a dead button to prospects.
    if (!link) return null;

    return (
        <>
            {/* Tooltip card */}
            {open && (
                <div
                    className="fixed bottom-24 right-5 z-50 w-[300px] rounded-2xl bg-white shadow-2xl border border-border overflow-hidden animate-in slide-in-from-bottom-2 fade-in duration-200"
                    data-testid="whatsapp-card"
                >
                    <div className="bg-[#25D366] text-white px-5 py-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                            <WhatsappLogo size={22} weight="fill" />
                        </div>
                        <div>
                            <div className="font-semibold text-sm">Invisique™ Care</div>
                            <div className="text-[11px] opacity-90">Typically replies in minutes</div>
                        </div>
                        <button
                            className="ml-auto p-1 rounded hover:bg-white/10"
                            onClick={() => setOpen(false)}
                            aria-label="Close"
                            data-testid="whatsapp-close"
                        >
                            <X size={16} weight="bold" />
                        </button>
                    </div>
                    <div className="p-5 bg-secondary/40">
                        <div className="bg-white rounded-xl p-3 text-sm text-foreground shadow-sm">
                            Hi there 👋 Need help finding a certified doctor or learning about Invisique™ clear
                            aligners? Message us on WhatsApp.
                        </div>
                    </div>
                    <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full bg-[#25D366] hover:bg-[#1DA851] text-white text-center font-semibold py-3 transition-colors"
                        data-testid="whatsapp-start-chat"
                    >
                        Start chat
                    </a>
                </div>
            )}

            {/* Floating button */}
            <button
                onClick={() => setOpen(!open)}
                className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white shadow-2xl flex items-center justify-center transition-all hover:scale-110 focus:outline-none focus:ring-4 focus:ring-[#25D366]/30"
                aria-label={open ? "Close WhatsApp chat" : "Open WhatsApp chat"}
                data-testid="whatsapp-fab"
            >
                {open ? <X size={24} weight="bold" /> : <WhatsappLogo size={28} weight="fill" />}
                {!open && (
                    <span className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-white animate-pulse" />
                )}
            </button>
        </>
    );
}
